import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import FooterNav from '../components/ui/footer-nav';
import ModuleRail from '../components/ui/module-rail';
import HeaderTasksButton from '../components/ui/header-tasks-button';
import { ArrowLeft, Clock, Loader2, AlertTriangle, Edit2, Save, Copy, Check, Users, Hash, MapPin, Phone, Mail, DollarSign, Briefcase, MessageSquare, ArrowRight, Calendar, X, Trophy, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LeadDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showQuotePopup, setShowQuotePopup] = useState(false);

  // Edit form state
  const [editForm, setEditForm] = useState({
    summary_text: '',
    client_name: '',
    contact_person: '',
    phone: '',
    email: '',
    additional_contacts: '',
    site_location: '',
    timing: '',
    follow_up_by: '',
    estimated_value: '',
    project_notes: '',
    referral_notes: '',
    raw_notes: ''
  });

  useEffect(() => {
    fetchLead();
  }, [id]);

  const fetchLead = async () => {
    try {
      const response = await axios.get(`${API_URL}/leads/${id}`);
      setLead(response.data);
      setEditForm({
        summary_text: response.data.summary_text || '',
        client_name: response.data.client_name || '',
        contact_person: response.data.contact_person || '',
        phone: response.data.phone || '',
        email: response.data.email || '',
        additional_contacts: response.data.additional_contacts || '',
        site_location: response.data.site_location || '',
        timing: response.data.timing || '',
        follow_up_by: response.data.follow_up_by || '',
        estimated_value: response.data.estimated_value || '',
        project_notes: response.data.project_notes || '',
        referral_notes: response.data.referral_notes || '',
        raw_notes: response.data.raw_notes || ''
      });
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to load lead');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!editForm.summary_text.trim()) {
      toast.error('Summary text is required');
      return;
    }

    setSaving(true);
    try {
      await axios.patch(`${API_URL}/leads/${id}`, editForm);
      toast.success('Lead updated!');
      setIsEditing(false);
      fetchLead();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/leads/${id}`);
      toast.success('Deleted successfully');
      navigate('/tradie/win');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
      setShowDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleCancel = () => {
    setEditForm({
      summary_text: lead.summary_text || '',
      client_name: lead.client_name || '',
      contact_person: lead.contact_person || '',
      phone: lead.phone || '',
      email: lead.email || '',
      additional_contacts: lead.additional_contacts || '',
      site_location: lead.site_location || '',
      timing: lead.timing || '',
      follow_up_by: lead.follow_up_by || '',
      estimated_value: lead.estimated_value || '',
      project_notes: lead.project_notes || '',
      referral_notes: lead.referral_notes || '',
      raw_notes: lead.raw_notes || ''
    });
    setIsEditing(false);
  };

  const handleCopyText = async () => {
    if (!lead?.summary_text) return;
    
    const textArea = document.createElement('textarea');
    textArea.value = lead.summary_text;
    textArea.style.position = 'fixed';
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.width = '2em';
    textArea.style.height = '2em';
    textArea.style.padding = '0';
    textArea.style.border = 'none';
    textArea.style.outline = 'none';
    textArea.style.boxShadow = 'none';
    textArea.style.background = 'transparent';
    textArea.style.opacity = '0';
    
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    textArea.setSelectionRange(0, lead.summary_text.length);
    
    let success = false;
    
    try {
      success = document.execCommand('copy');
    } catch (err) {
      console.error('execCommand copy failed:', err);
    }
    
    if (!success && navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(lead.summary_text);
        success = true;
      } catch (err) {
        console.error('Clipboard API failed:', err);
      }
    }
    
    document.body.removeChild(textArea);
    
    if (success) {
      setCopied(true);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error('Could not copy. Try selecting the text manually.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-100 flex flex-col items-center justify-center p-4">
        <AlertTriangle className="w-12 h-12 text-red-500 mb-4" />
        <p className="text-slate-600 mb-4">{error}</p>
        <Button onClick={() => navigate('/tradie/win')}>Go Back</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 pb-20 page-enter">
      {/* Header */}
      <header className="bg-[#1F2A37] text-white p-4 shadow-lg sticky top-0 z-10">
        <div className="h-1 bg-emerald-500 absolute top-0 left-0 right-0"></div>
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              data-testid="back-btn"
              onClick={() => navigate('/tradie/leads-referral', { state: { scrollToTasks: true } })}
              className="text-white hover:bg-slate-700"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <Trophy className="w-7 h-7 text-emerald-400" />
            <div>
              <h1 className="font-heading text-xl font-bold tracking-tight">
                {isEditing ? 'Edit Lead' : (lead.entry_type_display || 'Lead')}
              </h1>
              <p className="text-emerald-400 text-xs font-semibold uppercase tracking-wide">WIN WORK</p>
            </div>
          </div>
          {!isEditing ? (
            <div className="flex gap-2">
              <Button
                variant="ghost"
                data-testid="edit-btn"
                onClick={() => setIsEditing(true)}
                className="text-purple-300 hover:text-purple-200 hover:bg-slate-700"
              >
                <Edit2 className="w-4 h-4 mr-2" />
                Edit
              </Button>
              <Button
                variant="ghost"
                data-testid="delete-btn"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={deleting}
                className="text-red-400 hover:text-red-300 hover:bg-slate-700"
              >
                {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
                Delete
              </Button>
            </div>
          ) : (
            <div className="flex gap-2">
              <Button
                variant="ghost"
                data-testid="cancel-edit-btn"
                onClick={handleCancel}
                className="text-slate-300 hover:text-white hover:bg-slate-700"
              >
                Cancel
              </Button>
              <Button
                data-testid="save-edit-btn"
                onClick={handleSave}
                disabled={saving}
                className="bg-purple-500 hover:bg-purple-600"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                Save
              </Button>
            </div>
          )}
        </div>
      </header>

      {/* Module Rail */}
      <ModuleRail stage="win" activeModule="lead" />

      {/* Content */}
      <main className="p-4 max-w-2xl mx-auto">
        <Card className="border-2 border-purple-200 shadow-lg">
          <CardHeader className="pb-3">
            {!isEditing ? (
              <>
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading text-xl flex items-center gap-2">
                    <Users className="w-5 h-5 text-purple-500" />
                    {lead.entry_type_display || 'Lead'}
                  </CardTitle>
                  <span className="flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-mono">
                    <Hash className="w-3 h-3" />
                    {lead.reference_id}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {lead.work_type_display && (
                    <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                      {lead.work_type_display}
                    </span>
                  )}
                  {lead.source_display && (
                    <span className="px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-xs font-medium">
                      {lead.source_display}
                    </span>
                  )}
                  {lead.estimated_value && (
                    <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                      {lead.estimated_value}
                    </span>
                  )}
                </div>
              </>
            ) : (
              <CardTitle className="font-heading text-lg text-purple-600">Edit Your Lead</CardTitle>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {isEditing ? (
              /* Edit Mode */
              <div className="space-y-4">
                {/* Summary Text */}
                <div className="space-y-2">
                  <Label className="font-semibold">Summary <span className="text-red-500">*</span></Label>
                  <Textarea
                    data-testid="edit-summary"
                    value={editForm.summary_text}
                    onChange={(e) => setEditForm(prev => ({ ...prev, summary_text: e.target.value }))}
                    placeholder="Lead summary..."
                    className="min-h-[200px]"
                    required
                  />
                </div>

                {/* Contact Details */}
                <div className="pt-4 border-t border-slate-200">
                  <h3 className="text-sm font-semibold text-slate-500 mb-3">Contact Details</h3>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-sm">Client Name</Label>
                      <Input
                        data-testid="edit-client-name"
                        value={editForm.client_name}
                        onChange={(e) => setEditForm(prev => ({ ...prev, client_name: e.target.value }))}
                        placeholder="Business / Client"
                        className="h-10"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-sm">Contact Person</Label>
                      <Input
                        data-testid="edit-contact-person"
                        value={editForm.contact_person}
                        onChange={(e) => setEditForm(prev => ({ ...prev, contact_person: e.target.value }))}
                        placeholder="Name"
                        className="h-10"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <div className="space-y-1">
                      <Label className="text-sm">Phone</Label>
                      <Input
                        data-testid="edit-phone"
                        value={editForm.phone}
                        onChange={(e) => setEditForm(prev => ({ ...prev, phone: e.target.value }))}
                        placeholder="0412 345 678"
                        className="h-10"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-sm">Email</Label>
                      <Input
                        data-testid="edit-email"
                        value={editForm.email}
                        onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                        placeholder="email@example.com"
                        className="h-10"
                      />
                    </div>
                  </div>

                  {/* Additional Contacts */}
                  <div className="space-y-1 mt-3">
                    <Label className="text-sm">Additional Contacts <span className="text-slate-400 font-normal">(optional)</span></Label>
                    <Textarea
                      data-testid="edit-additional-contacts"
                      value={editForm.additional_contacts}
                      onChange={(e) => setEditForm(prev => ({ ...prev, additional_contacts: e.target.value }))}
                      placeholder="e.g. Site super: Dave 0412 111 222, Office: Sarah sarah@company.com"
                      className="min-h-[60px]"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <div className="space-y-1">
                      <Label className="text-sm">Location</Label>
                      <Input
                        data-testid="edit-location"
                        value={editForm.site_location}
                        onChange={(e) => setEditForm(prev => ({ ...prev, site_location: e.target.value }))}
                        placeholder="Suburb"
                        className="h-10"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-sm">Est. Value</Label>
                      <Input
                        data-testid="edit-value"
                        value={editForm.estimated_value}
                        onChange={(e) => setEditForm(prev => ({ ...prev, estimated_value: e.target.value }))}
                        placeholder="$5k-10k"
                        className="h-10"
                      />
                    </div>
                  </div>

                  <div className="space-y-1 mt-3">
                    <Label className="text-sm">Timing / Urgency</Label>
                    <Input
                      data-testid="edit-timing"
                      value={editForm.timing}
                      onChange={(e) => setEditForm(prev => ({ ...prev, timing: e.target.value }))}
                      placeholder="Next month, ASAP, etc."
                      className="h-10"
                    />
                  </div>

                  <div className="space-y-1 mt-3">
                    <Label className="text-sm">Follow Up By</Label>
                    <Input
                      data-testid="edit-follow-up-by"
                      type="date"
                      value={editForm.follow_up_by}
                      onChange={(e) => setEditForm(prev => ({ ...prev, follow_up_by: e.target.value }))}
                      className="h-10"
                    />
                  </div>
                </div>

                {/* Notes */}
                <div className="space-y-2">
                  <Label className="text-sm">Anything Else About the Project? <span className="text-slate-400 font-normal">(optional)</span></Label>
                  <Textarea
                    data-testid="edit-project-notes"
                    value={editForm.project_notes}
                    onChange={(e) => setEditForm(prev => ({ ...prev, project_notes: e.target.value }))}
                    placeholder="Add any extra project detail if needed..."
                    className="min-h-[80px]"
                  />
                </div>

                {lead.referral_approach && (
                  <div className="space-y-2">
                    <Label className="text-sm">Referral Notes</Label>
                    <Textarea
                      data-testid="edit-referral-notes"
                      value={editForm.referral_notes}
                      onChange={(e) => setEditForm(prev => ({ ...prev, referral_notes: e.target.value }))}
                      placeholder="Referral arrangement details..."
                      className="min-h-[80px]"
                    />
                  </div>
                )}
              </div>
            ) : (
              /* View Mode */
              <>
                {/* Date/Time */}
                <div className="flex items-center gap-2 text-slate-500">
                  <Clock className="w-4 h-4" />
                  <span className="text-sm">
                    {format(new Date(lead.created_at), 'EEEE, MMMM d, yyyy - h:mm a')}
                  </span>
                </div>

                {/* Quick Info Cards */}
                {(lead.client_name || lead.contact_person || lead.phone || lead.email || lead.site_location) && (
                  <div className="grid grid-cols-2 gap-3 p-3 bg-slate-50 rounded-lg">
                    {lead.client_name && (
                      <div className="flex items-start gap-2">
                        <Briefcase className="w-4 h-4 text-slate-400 mt-0.5" />
                        <div>
                          <p className="text-xs text-slate-500">Client</p>
                          <p className="text-sm font-medium text-slate-700">{lead.client_name}</p>
                        </div>
                      </div>
                    )}
                    {lead.contact_person && (
                      <div className="flex items-start gap-2">
                        <Users className="w-4 h-4 text-slate-400 mt-0.5" />
                        <div>
                          <p className="text-xs text-slate-500">Contact</p>
                          <p className="text-sm font-medium text-slate-700">{lead.contact_person}</p>
                        </div>
                      </div>
                    )}
                    {lead.phone && (
                      <div className="flex items-start gap-2">
                        <Phone className="w-4 h-4 text-slate-400 mt-0.5" />
                        <div>
                          <p className="text-xs text-slate-500">Phone</p>
                          <a href={`tel:${lead.phone}`} className="text-sm font-medium text-purple-600">{lead.phone}</a>
                        </div>
                      </div>
                    )}
                    {lead.email && (
                      <div className="flex items-start gap-2">
                        <Mail className="w-4 h-4 text-slate-400 mt-0.5" />
                        <div>
                          <p className="text-xs text-slate-500">Email</p>
                          <a href={`mailto:${lead.email}`} className="text-sm font-medium text-purple-600 break-all">{lead.email}</a>
                        </div>
                      </div>
                    )}
                    {lead.site_location && (
                      <div className="flex items-start gap-2">
                        <MapPin className="w-4 h-4 text-slate-400 mt-0.5" />
                        <div>
                          <p className="text-xs text-slate-500">Location</p>
                          <p className="text-sm font-medium text-slate-700">{lead.site_location}</p>
                        </div>
                      </div>
                    )}
                    {lead.estimated_value && (
                      <div className="flex items-start gap-2">
                        <DollarSign className="w-4 h-4 text-slate-400 mt-0.5" />
                        <div>
                          <p className="text-xs text-slate-500">Est. Value</p>
                          <p className="text-sm font-medium text-green-600">{lead.estimated_value}</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Additional Contacts */}
                {lead.additional_contacts && (
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <p className="text-xs text-slate-500 font-medium mb-1">Additional Contacts</p>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">{lead.additional_contacts}</p>
                  </div>
                )}

                {/* Timing */}
                {(lead.timing || lead.follow_up_by) && (
                  <div className="flex flex-wrap items-center gap-3 p-3 bg-purple-50 rounded-lg">
                    {lead.timing && (
                      <div className="flex items-center gap-2 text-purple-600">
                        <Clock className="w-4 h-4" />
                        <span className="font-medium">Timing: {lead.timing}</span>
                      </div>
                    )}
                    {lead.follow_up_by && (
                      <div className="flex items-center gap-2 text-purple-700">
                        <Calendar className="w-4 h-4" />
                        <span className="font-medium">
                          Follow Up By: {new Date(lead.follow_up_by).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Referral Approach */}
                {lead.referral_approach_display && (
                  <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                    <p className="text-xs text-purple-600 font-medium mb-1">Referral Approach</p>
                    <p className="text-sm text-purple-800">{lead.referral_approach_display}</p>
                    {lead.referral_notes && (
                      <p className="text-sm text-purple-700 mt-2 pt-2 border-t border-purple-200">{lead.referral_notes}</p>
                    )}
                  </div>
                )}

                {/* Summary Text */}
                <div className="pt-4 border-t border-slate-200">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-slate-700">Summary</h4>
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid="copy-summary-btn"
                      onClick={handleCopyText}
                      className={copied ? "bg-green-100 border-green-300" : ""}
                    >
                      {copied ? (
                        <>
                          <Check className="w-4 h-4 mr-1 text-green-600" />
                          <span className="text-green-600">Copied</span>
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4 mr-1" />
                          Copy
                        </>
                      )}
                    </Button>
                  </div>
                  <p className="text-slate-600 whitespace-pre-wrap bg-slate-50 p-4 rounded-lg text-sm">
                    {lead.summary_text}
                  </p>
                </div>

                {/* Footer Note */}
                <div className="pt-4 border-t border-slate-200">
                  <div className="flex items-start gap-2 p-3 bg-slate-50 rounded-lg">
                    <MessageSquare className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-slate-500 leading-relaxed">
                      Want a signature added, PDF, email issued or custom setup? Talk to our team about adding more capability to your 2Tap setup.
                    </p>
                  </div>
                </div>

                {/* Convert to Quote CTA */}
                <div className="pt-4">
                  <Button
                    data-testid="convert-to-quote-btn"
                    onClick={() => setShowQuotePopup(true)}
                    className="w-full h-14 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white text-base font-semibold shadow-md"
                  >
                    <ArrowRight className="w-5 h-5 mr-2" />
                    Convert to Quote
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Convert to Quote Popup */}
      {showQuotePopup && (
        <div 
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
          onClick={() => setShowQuotePopup(false)}
        >
          <div 
            className="bg-white rounded-xl shadow-xl max-w-sm w-full"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              <h3 className="font-semibold text-lg text-slate-800">Convert to Quote?</h3>
              <button 
                onClick={() => setShowQuotePopup(false)}
                className="p-1.5 hover:bg-slate-100 rounded-full transition-colors"
              >
                <X className="w-4 h-4 text-slate-400" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4">
              <p className="text-slate-600 text-sm leading-relaxed">
                Let's turn this into a Quick Quote so you can respond faster without things backing up.
              </p>
              <p className="text-slate-600 text-sm leading-relaxed mt-3">
                Talk to our team about adding this capability to your 2Tap setup.
              </p>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-slate-100">
              <Button
                onClick={() => {
                  setShowQuotePopup(false);
                  toast.success('Thanks! Our team will be in touch.');
                }}
                className="w-full bg-purple-500 hover:bg-purple-600 h-11"
              >
                Talk to our team
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full shadow-xl">
            <h3 className="text-lg font-bold text-slate-800 mb-2">Delete Lead?</h3>
            <p className="text-slate-600 mb-6">Are you sure you want to delete this lead? This cannot be undone.</p>
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleDelete}
                disabled={deleting}
                className="bg-red-500 hover:bg-red-600 text-white"
              >
                {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
      
      <FooterNav />
    </div>
  );
};

export default LeadDetail;
